import { reactive, } from 'vue'
import { doc, } from "firebase/firestore";
import * as ser from './Common/SerUtil.js'
import { makeEnum, makeEnumWithData, makeOptions,
  makeEnumWithDataAndLabels,
  setupClass, lookupData, Matches,
  interpolateInMap, doubleInterpolateInMap,
  IdsMap, PleaseContactStr,
  IntervalTimer,
} from './Base.js'
import { ProjectUnits } from './Common/Field.js'
import { ProjectErrors } from './ProjectErrors.js';

import { prettyJson, clearArray, valOr, getElemNames,
  getElemWithNameValue,
  addElem, removeElem, elemIn,
  hashObject,
  downloadTextFile,
} from './SharedUtils.js'
import { FirebaseUtils } from './Common/FirebaseUtils.js'

import { ProjectLock } from './Common/ProjectLock.js'
import { Assistant } from './Assistant/Assistant.js'
import { Watcher } from './Common/Watcher.js'
import { ErrorCollector } from './Common/ErrorCollector.js'

import { gApp, DebugOn, IsTestEnv, } from './Globals.js'

export let ProjectTypes = makeEnum({
  Residential: "Residential",
  Commercial: "Commercial",
})

class EditorState {
  constructor() {
    /**
     * Note: these are not used right now. The 'currentItem' state is currently stored in
     * the ComponentView itself. Not ideal. May need to implement these eventually.
     */
    this.selectedWall = null;
    this.selectedRoof = null;
    this.selectedWindow = null;
    this.selectedSkylight = null;
    this.selectedInternalShading = null;
    this.selectedExternalShading = null;
    this.selectedBufferSpace = null;
    this.selectedSchedule = null;
    this.selectedSpace = null;
    this.selectedSystem = null;
    this.selectedSystemForResults = null;
  }
}

/**
 * Project base-class
 */
export class Project {
  /**
   * Note - we must pass in id and units in the ctor because they absolutely must be
   * set before calling 'setup()'. The setup() function creates Fields and child components
   * that rely on those fields. 
   */
  init(id, units, opts) {
    opts = valOr(opts, {});

    this.id = id;
    this.units = units || ProjectUnits.Imperial;
    // If the project is new, setCurProj runs setupNewProject()
    this.isNew = false;
    this.useMockTimes = IsTestEnv();
    // This gets set to true by toplevel App once the Project has been successfully
    // loaded and is ready to be edited + saved.
    this.doneLoading = false;
    // False until the user has seen the welcome message (via modal)
    // Note: default to true so that existing projects don't show it
    this.shownWelcomeMessage = true;
    // This is the project owner id and email
    this.userId = 0;
    this.userEmail = "Unknown";
    this.name = "MyProj";
    this.type = ProjectTypes.Residential;
    this.sharedUsers = [];
    this.lockedBy = null;
    this.lastOpenedTime = this.useMockTimes ? 1000 : Date.now();
    this.saving = false;
    this.lastSaveHash = null;
    this.debugOn = false;
    this.helpPaneId = null;
    this.helpPaneVisible = false;
    this.editorState = reactive(new EditorState());

    this.idsMap = new IdsMap();
    this.watcher = new Watcher({name: "ProjectWatcher"});

    this.projectErrors = ProjectErrors.create();

    // True when this project is created in a web worker, for
    // the sole purpose of running a calculation.
    this.workerProject = valOr(opts.workerProject, false);
    if (!this.workerProject) {
      this.checkLockTimer = new IntervalTimer(() => {
        this.updateLockStatus();
      }, 2, {onlyWhenVisible: true})
    }

    this.updateProjectErrorsTimer = null;

    // Subclasses can set a 'guide' object to help with
    // onboarding and showing the user how to use the app.
    this.guide = null;
    // Tracks whether the user has modified the location
    // data at all. Used to determine if the guide step is done.
    this.hasTouchedLocation = false;

    // This is the version of the app when this project was created.
    // We didn't start with versioning, so we default to 1.0.0
    // for projects that don't have this field set.
    this.projectVersion = '1.0.0';

    // The AI assistant state
    this.assistant = Assistant.create()

    this.childObjs = '$auto'
  }

  get serFields() {
    return [
      'isNew',
      'userId',
      'userEmail',
      'name',
      'type',
      'units',
      'sharedUsers',
      'lastOpenedTime',
      'debugOn',
      'idsMap',
      'shownWelcomeMessage',
      'projectVersion',
      'assistant',
    ];
  }

  async setupNewProject() {
    // Call this upon creating a new project. Sets some defaults correctly
    console.log("Setting up new project...");
    this.projectVersion = gApp.getAppVersion();
    await this.onSetupNewProject();
    this.isNew = false;
  }

  async onSetupNewProject() {
    // Override in subclasses
  }

  async onPostReadFromJson() {
    // Override in subclasses
  }

  getProjectVersion() {
    return this.projectVersion;
  }

  getProjectType() {
    return this.type;
  }

  isCommercial() {
    return this.type == ProjectTypes.Commercial;
  }

  isResidential() {
    return this.type == ProjectTypes.Residential;
  }

  getProjectUnits() {
    return this.units;
  }

  debug_setProjectUnits(units) {
    // TODO-metric - need to traverse object hierarchy and call sync for all fields
    // This is only supported for debug purposes right now.
    this.units = units;
  }

  isImperial() {
    return this.units == ProjectUnits.Imperial;
  }

  isMetric() {
    return this.units == ProjectUnits.Metric;
  }

  isDoneLoading() {
    return this.doneLoading;
  }

  setDoneLoading() {
    this.doneLoading = true;
  }

  hasShownWelcomeMessage() {
    return this.shownWelcomeMessage;
  }

  setShownWelcomeMessage() {
    this.shownWelcomeMessage = true;
  }

  setHasTouchedLocation(hasTouched) {
    this.hasTouchedLocation = hasTouched;
  }

  resetGuide() {
    if (this.guide) {
      this.guide.reset();
      this.hasTouchedLocation = false;
    }
  }

  getName() {
    return this.name;
  }

  getBaseRoute() {
    return new Error("Override in subclass");
  }

  makeId(typeName) {
    return this.idsMap.makeId(typeName);
  }

  generateUid(prefix) {
    return `${prefix}-${this.makeId(prefix)}`;
  }

  /*
  Use this as a central place to add watch effects that should be scoped to the project.
  They will be removed when the project is destroyed.
  */
  addWatchEffect(effectFunc, opts) {
    return this.watcher.addWatchEffect(effectFunc, opts);
  }

  async close() {
    console.log("CLOSING PROJECT: " + this.name);
    console.log("Unregistering watchers...");
    this.watcher.unwatchAll();

    console.log("Updating timers...");
    if (this.checkLockTimer) {
      this.checkLockTimer.stop();
    }

    this.stopProjectErrorsUpdater();

    await this.onCloseProject();

    console.log("Saving project...");
    await this.save();

    console.log("Releasing lock...");
    await ProjectLock.tryReleaseLock(this.id);

    console.log("DONE");
  }

  async onCloseProject() {
    // Note: override in subclasses
  }

  getUiName() {
    return this.name || "Untitled-Project";
  }

  getDateString() {
    return (new Date()).toLocaleDateString();
  }

  getHelpId() {
    return this.helpPaneId;
  }

  setHelpId(helpId, open) {
    this.helpPaneId = helpId;
    if (valOr(open, false)) {
      this.helpPaneVisible = true;
    }
  }
  
  setHelpIdRef(helpIdRef, open) {
    // TODO - not yet implemented
    // Jump to a specific help section within the current helpId
  }

  setHelpIdForPath(routePath) {
    // Note - this called when the route changes (from ProjectView.vue)
    //console.log("New path: ", newPath);
    let isCommercial = this.isCommercial();
    let helpIds = {
      '': isCommercial ? 'Commercial_Overview_Page_Overview_Page' : 'Residential_Overview_Overview',
      'build': 'Residential_Build_House_BuildHouse',
      'walls': 'Shared_Walls_Page_Walls',
      'roofs': 'Shared_Roofs_Page_Roofs',
      'windows': 'Shared_Windows_Page_Windows',
      'doors': 'Shared_Doors_Page_Doors',
      'skylights': 'Shared_Skylights_Page_Skylights',
      'internalshadings': 'Shared_Internal_Shading_Page_Internal_Shades',
      'externalshadings': 'Shared_External_Shading_Page_External_Shades',
      'bufferspaces': 'Shared_Buffer_Spaces_Page_Buffer_Spaces',
      'schedules': 'Commercial_Schedules_Page_Schedules',
      'location': 'Commercial_Location_Location',
      'systems': 'Commercial_Systems_Page_Systems',
      'spaces': 'Commercial_Spaces_Page_Spaces',
      'import': 'Commercial_Import_Page_Import',
      'assistant': 'Shared_Assistant_Page_Assistant',
      'results': isCommercial ? 'Commercial_Results_Results' : 'Residential_Results_Results',
    }
    let pathParts = routePath.split('/');
    let pageId = null;
    if (pathParts.length == 3) {
      pageId = '';
    } else if (pathParts.length >= 4) {
      pageId = pathParts[3];
    }
    if (pageId in helpIds) {
      this.setHelpId(helpIds[pageId]);
      console.log("Set help id to " + helpIds[pageId]);
    } else {
      console.log(`No help id for path: ${routePath} (id="${pageId}")`);
      this.setHelpId(null)
      this.setHelpPaneVisible(false);
    }
  }

  setHelpPaneVisible(isVisible) {
    this.helpPaneVisible = isVisible;
  }

  async updateLockStatus() {
    /*
    // TODO - this is for when read+write support comes
    let lockData = await ProjectLock.getLockData(this.id);
    console.log("UPDATING LOCK STATUS: ", lockData);
    this.lockedBy = lockData ? lockData.userEmail : null;
    */
    let curOwner = await ProjectLock.updateOwnership(this.id);
    if (curOwner !== gApp.getUser().email) {
      console.log(`Another user (${curOwner}) booted you from the project!`);
      gApp.router.replace('/');
    }
    this.lockedBy = curOwner
  }

  getProjectJson() {
    return ser.writeToJson(this, {debug: false});
  }

  exportToFile() {
    console.log("Exporting project to file");
    let projectData = this.getProjectJson();
    // Get a date string of the form YYYY-MM-DD
    let now = new Date();
    let dateStr = new Intl.DateTimeFormat('en-CA').format(now);
    let fileName = `${this.name}_${dateStr}.hwise`;
    downloadTextFile(prettyJson(projectData), fileName);
    gApp.toast("Project exported");
  }

  async save() {
    if (!this.doneLoading) {
      console.log("Project not yet done loading. Ignoring save.");
      return;
    }
    if (this.saving) {
      console.log("Already saving proj.")
      return;
    }
    this.saving = true;
    try {
      // We only save if something has changed (indicated by a changed
      // hash value). It's cheap to serialize the project, so can check
      // this often.
      // console.log("Checking if save required: " + this.id);

      let debug = true;
      let jsonData = ser.writeToJson(this, {debug: false});
      let hashVal = hashObject(jsonData);
      if (!this.lastSaveHash || hashVal !== this.lastSaveHash) {
        console.log(`Project change detected for Proj ${this.id}. Saving. ${this.lastSaveHash} -> ${hashVal}`);
        if (debug) {
          //console.log("Save data: ", prettyJson(jsonData));
          console.log(`Project data: `, jsonData);
        }

        // Check that user has the lock
        let hasLock = await ProjectLock.hasLock(this.id);
        if (!hasLock) {
          console.log(`Ignoring save. Does not have lock for ${this.id}`);
          return;
        }

        let fbUtils = new FirebaseUtils(gApp.getUser());
        await fbUtils.setDoc(doc(gApp.db, 'projects', this.id), jsonData, 'save-project');
        console.log("Save complete");
        this.lastSaveHash = hashVal;
      }
    } finally {
      this.saving = false;
    }
  }

  async updateLastOpenedTime() {
    console.log("Updating lastOpenedTime");
    if (this.useMockTimes) {
      return;
    }
    this.lastOpenedTime = Date.now();
    let fbUtils = new FirebaseUtils(gApp.getUser());
    await fbUtils.updateDoc(doc(gApp.db, 'projects', this.id), {
      lastOpenedTime: this.lastOpenedTime,
    }, 'update-project-last-opened-time'); 
  }

  async updateSharedUsers() {
    console.log("Updating sharedUsers");
    let fbUtils = new FirebaseUtils(gApp.getUser());
    await fbUtils.updateDoc(doc(gApp.db, 'projects', this.id), {
      sharedUsers: this.sharedUsers,
    }, 'update-project-shared-users'); 
  }

  addSharedUser(userEmail) {
    console.log("Adding shared user!");
    if (userEmail == this.userEmail) {
      gApp.toast(`${userEmail} can already access the project.`, {type: 'error'})
      return;
    }
    if (!elemIn(userEmail, this.sharedUsers)) {
      this.sharedUsers.push(userEmail);
    }
    this.updateSharedUsers();
    gApp.toast(`Shared the project with: ${userEmail}`)
    gApp.sendUserNotif(userEmail, {type: 'AddedToProject', owner: this.userEmail, name: this.name})
  }

  removeSharedUser(userEmail) {
    removeElem(this.sharedUsers, userEmail);
    this.updateSharedUsers();
    gApp.toast(`Removed ${userEmail} from the project.`);
    gApp.sendUserNotif(userEmail, {type: 'RemovedFromProject', owner: this.userEmail, name: this.name})
  }

  async rename(newName) {
    this.name = newName;
    let fbUtils = new FirebaseUtils(gApp.getUser());
    await fbUtils.updateDoc(doc(gApp.db, 'projects', this.id), {
      name: this.name,
    }, 'rename-project'); 
    gApp.toast(`Renamed project`)
  }

  /*
  Null if no errors. Otherwise, an array of error strings.
  */
  getProjectErrors() {
    return this.projectErrors;
  }

  // Whether we're in the "error debugging" state, where project errors from a
  // previous calculation attempt are populated and being shown to the user.
  isShowingProjectErrors() {
    return !this.getProjectErrors().isEmpty();
  }

  updateProjectErrors(opts) {
    /*
    Note: we run this before calculating the results, and store the errors. We do not want to make this reactive b/c
    , the way it is setup with probing the objects, it will update whenever any field changes in the object hierarchy.
    We used to run this every second, but changed to now only run before calculations are run (more sensible).
    */
    // Visit child objects hierarchy to collect all errors
    // console.log("===============UPDATING PROJECT ERRORS===============");
    let errorCollector = new ErrorCollector(this, opts);
    let errors = errorCollector.getErrors();
    // console.log("===============DONE===============");
    this.projectErrors.setErrors(errors);

    // If we have errors, start the update timer so that the projectErrors
    // dict updates automatically as the user fixes things.
    if (!this.projectErrors.isEmpty()) {
      this.startProjectErrorsUpdater(opts);
    } else {
      // Stop if no errors
      this.stopProjectErrorsUpdater();
    }
    console.log("New Project Errors: ", this.projectErrors);
    return this.projectErrors;
  }

  startProjectErrorsUpdater(opts) {
    if (this.workerProject) {
      // Don't start the timer if this is a worker project
      console.log("Not starting project errors updater. Worker project.");
      return;
    }

    // Restarts the timer with the new opts, if already running.
    this.stopProjectErrorsUpdater();

    // When enabled, this will run every X secs and update the project errors.
    // Turn on when debugging project errors.
    //console.log("Starting project errors updater...");
    this.updateProjectErrorsTimer = new IntervalTimer(() => {
      //console.log("Updating project errors...");
      this.updateProjectErrors(opts);
    }, 0.500, {onlyWhenVisible: true});
  }

  stopProjectErrorsUpdater() {
    if (this.updateProjectErrorsTimer) {
      this.updateProjectErrorsTimer.stop();
      this.updateProjectErrorsTimer = null;
    }
  }

  isPagePathHighlighted(pagePath) {
    // Highlight page paths for the Guide steps
    if (!this.guide) {
      return false;
    }
    let highlightedPagePath = this.guide.getHighlightedPagePath();
    return pagePath === highlightedPagePath;
  }

  isGuideComplete() {
    if (!this.guide) {
      return true;
    }
    return this.guide.allDone();
  }

  getAssistant() {
    return this.assistant
  }

  getLocationData() {
    // Override in subclass
    throw new Error("Override in subclass");
  }

  getSampleTemps() {
    // Used to get sample temperatures for use in the UI, before
    // final calculations are done
    // Override in subclass
    throw new Error("Override in subclass");
  }
};
setupClass(Project)